import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
}

const Button: React.FC<ButtonProps> = React.memo(
  ({
    children,
    variant = "primary",
    size = "md",
    isLoading = false,
    className = "",
    disabled,
    ...props
  }) => {
    const baseStyles =
      "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";

    const variants = {
      primary:
        "bg-primary-default text-white hover:bg-primary-light focus:ring-primary-light shadow-sm hover:shadow-md transition-all duration-200",
      secondary:
        "bg-secondary-default text-white hover:bg-secondary-light focus:ring-secondary-light shadow-sm hover:shadow-md transition-all duration-200",
      danger:
        "bg-danger-default text-white hover:bg-danger-light focus:ring-danger-light shadow-sm hover:shadow-md transition-all duration-200",
    };

    const sizes = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    const loadingSpinner = (
      <svg
        className="animate-spin -ml-1 mr-2 h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    return (
      <button
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${
          disabled || isLoading ? "opacity-50 cursor-not-allowed" : ""
        } ${className} animate-fade-in`}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && loadingSpinner}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
